import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Loader2, Search, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { adminApiTokensApi } from '@/lib/admin-api-tokens-api';
import { getErrorMessage } from '@/lib/errors';
import type { AdminApiToken } from '@/types/api';

const KEY = ['admin-api-tokens'];

const STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'expired', label: 'Expired' },
];

/**
 * Every API token on the platform, for staff.
 *
 * Read and revoke. There is deliberately no create: that would be a long-lived credential
 * authenticating as another tenant, and impersonation already covers staff who need to act as a
 * customer — scoped, expiring, and recorded as impersonation rather than indistinguishable from
 * the customer's own traffic.
 */
export function AdminApiTokensPage() {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [confirming, setConfirming] = useState<AdminApiToken | null>(null);

  const tokens = useQuery({
    queryKey: [...KEY, search, status],
    queryFn: () => adminApiTokensApi.list({ search, status }),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => adminApiTokensApi.revoke(id),
    onSuccess: () => {
      setConfirming(null);
      void queryClient.invalidateQueries({ queryKey: KEY });
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">API tokens</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Every token on the platform. The token itself is never shown. Only a hash of it is
          stored, so it cannot be recovered by anyone, including you.
        </p>
      </div>

      <Card>
        <CardContent className="py-4">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(searchInput.trim());
            }}
          >
            <div className="min-w-56 flex-1">
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Search API tokens by owner email, token name or prefix"
              />
            </div>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label="Status"
              className="w-44"
            >
              {STATUSES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Button type="submit" variant="outline">
              <Search /> Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {revoke.error !== null && <Alert variant="danger">{getErrorMessage(revoke.error)}</Alert>}
      {tokens.isError && <Alert variant="danger">{getErrorMessage(tokens.error)}</Alert>}

      <Card>
        <CardHeader>
          <CardTitle>
            {tokens.data === undefined ? 'Tokens' : `${tokens.data.meta.total} token(s)`}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {tokens.isLoading && (
            <p className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 className="size-4 animate-spin" /> Loading…
            </p>
          )}

          {tokens.data?.data.length === 0 && (
            <p className="text-muted-foreground text-sm">No tokens match these filters.</p>
          )}

          {tokens.data?.data.map((token) => (
            <TokenRow
              key={token.id}
              token={token}
              busy={revoke.isPending}
              onRevoke={() => setConfirming(token)}
            />
          ))}
        </CardContent>
      </Card>

      {confirming !== null && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setConfirming(null);
            }
          }}
          title={`Revoke "${confirming.name}"?`}
          description={`Anything using this token stops working immediately, including ${confirming.owner_email}'s own integrations. This cannot be undone. A new token has to be created by them.`}
          confirmLabel="Revoke"
          destructive
          busy={revoke.isPending}
          error={revoke.error}
          onConfirm={() => revoke.mutate(confirming.id)}
        />
      )}
    </div>
  );
}

function TokenRow({
  token,
  busy,
  onRevoke,
}: {
  token: AdminApiToken;
  busy: boolean;
  onRevoke: () => void;
}) {
  const live = token.status === 'active';

  return (
    <div
      className={`border-border bg-muted/20 flex flex-wrap items-start justify-between gap-3 rounded-md border px-4 py-3 ${
        live ? '' : 'opacity-70'
      }`}
    >
      <div className="min-w-0 space-y-1">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
          <KeyRound className="size-3.5 shrink-0" />
          {token.name}
          <Badge variant={live ? 'success' : 'muted'}>{token.status}</Badge>
          {token.owner_role !== 'customer' && <Badge variant="warning">{token.owner_role}</Badge>}
        </p>
        <p className="text-muted-foreground truncate text-xs">{token.owner_email}</p>
        {/* The first twelve characters, which is what a customer can read out of their own logs.
            The rest does not exist here — only a SHA-256 hash is stored. */}
        <p className="font-mono text-xs tracking-wider">{token.prefix}…</p>
        <p className="text-muted-foreground text-xs">
          Created {new Date(token.created_at).toLocaleDateString()}
          {' · '}
          {token.last_used_at === null
            ? 'never used'
            : `last used ${new Date(token.last_used_at).toLocaleString()}`}
          {token.expires_at !== null &&
            ` · expires ${new Date(token.expires_at).toLocaleDateString()}`}
          {token.revoked_at !== null &&
            ` · revoked ${new Date(token.revoked_at).toLocaleString()}`}
        </p>
        {token.abilities.length > 0 && (
          <p className="text-muted-foreground text-xs">Abilities: {token.abilities.join(', ')}</p>
        )}
      </div>

      {live && (
        <Button variant="ghost" size="sm" onClick={onRevoke} disabled={busy}>
          <ShieldOff className="size-3.5" /> Revoke
        </Button>
      )}
    </div>
  );
}
